import { type MouseEvent, useCallback, useContext, useEffect, useRef } from "react";

import { Box, Group } from "@mantine/core";
import { DateInput, type DateValue, TimeInput } from "@mantine/dates";
import type { UseFormReturnType } from "@mantine/form";

import { IconCalendar, IconClock } from "@tabler/icons-react";
import { isMatch } from "date-fns";

import { useStableCalendarDateInputState } from "@/intake-forms/hooks/useStableCalendarDateInputState";
import { setFormFieldValue } from "@/intake-forms/utils/intake-forms";
import { questionnaireFieldTestId } from "@/intake-forms/utils/questionnaire-field-testid";
import { localTimeFromISO } from "@/utils/datetime";
import { useControlledTimeInput } from "@/utils/hooks/useControlledTimeInput";
import { parseMantineDateInputValue } from "@/utils/mantine-date-input";
import {
  getCurrentLocalTimeString,
  localToUtcTimeString,
  utcToLocalTimeString,
} from "@/utils/timeUtils";

import { LaborFlowsheetUtcDateAnchorContext } from "./LaborFlowsheetUtcDateAnchorContext";

interface LaborFlowsheetDateTimeFieldsProps<T> {
  form: UseFormReturnType<T>;
  dateFieldName: keyof T & string;
  timeFieldName: keyof T & string;
  dateTimeGroupLinkId: string;
  dateLabel?: string;
  timeLabel?: string;
  /** When false, empty date/time stay empty (edit mode). Defaults to true for quick-add. */
  autoPopulateDateTime?: boolean;
}

export function LaborFlowsheetDateTimeFields<T>({
  form,
  dateFieldName,
  timeFieldName,
  dateTimeGroupLinkId,
  dateLabel = "Date",
  timeLabel = "Time",
  autoPopulateDateTime = true,
}: LaborFlowsheetDateTimeFieldsProps<T>) {
  const utcDateAnchors = useContext(LaborFlowsheetUtcDateAnchorContext);
  const utcDateAnchor = utcDateAnchors?.[dateTimeGroupLinkId];

  // The group-scoped path the questionnaire test-id registry keys these fields under, matching what
  // QuestionnaireDateField/QuestionnaireTimeField emit for the same linkIds. A flowsheet whose group
  // has no registry entry gets no attribute.
  const dateTestId = questionnaireFieldTestId(`${dateTimeGroupLinkId}.${dateFieldName}`);
  const timeTestId = questionnaireFieldTestId(`${dateTimeGroupLinkId}.${timeFieldName}`);

  const timeInputProps = form.getInputProps(timeFieldName);
  const dateInputProps = form.getInputProps(dateFieldName);
  const dateValue = dateInputProps.value as Date | null | undefined;

  const { stableDateValue, calendarDate, handleCalendarDateChange } =
    useStableCalendarDateInputState(dateValue);

  const hasAutoPopulatedDateRef = useRef(false);

  useEffect(() => {
    if (!autoPopulateDateTime || hasAutoPopulatedDateRef.current || dateValue) {
      return;
    }

    hasAutoPopulatedDateRef.current = true;
    setFormFieldValue(form, dateFieldName, new Date());
  }, [autoPopulateDateTime, dateFieldName, dateValue, form]);

  const handleDateChange = useCallback(
    (val: DateValue) => {
      if (!val) {
        setFormFieldValue(form, dateFieldName, undefined);
        return;
      }
      setFormFieldValue(form, dateFieldName, parseMantineDateInputValue(val));
    },
    [dateFieldName, form],
  );

  const setTimeFieldValue = useCallback(
    (value: string) => {
      setFormFieldValue(form, timeFieldName, value);
    },
    [form, timeFieldName],
  );

  const storedToDisplay = useCallback(
    (value: string) => {
      if (isMatch(value, "HH:mm:ss")) {
        if (utcDateAnchor && isMatch(utcDateAnchor, "yyyy-MM-dd")) {
          return localTimeFromISO(`${utcDateAnchor}T${value}Z`);
        }
        return utcToLocalTimeString(value);
      }
      return value;
    },
    [utcDateAnchor],
  );

  const getAutoPopulateValue = useCallback(() => {
    const localTimeString = getCurrentLocalTimeString();
    return {
      display: localTimeString,
      stored: localToUtcTimeString(localTimeString),
    };
  }, []);

  const { displayValue, inputRef, onFocus, onBlur, onChange, openPicker } = useControlledTimeInput({
    storedValue: timeInputProps.value as string | null | undefined,
    onCommit: setTimeFieldValue,
    storedToDisplay,
    displayToStored: localToUtcTimeString,
    autoPopulate: autoPopulateDateTime,
    getAutoPopulateValue,
  });

  const handleTimePickerClick = (event: MouseEvent) => {
    event.stopPropagation();
    openPicker();
  };

  const timeInputLeftSection = (
    <Box
      component="span"
      onClick={handleTimePickerClick}
      style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
    >
      <IconClock size={20} stroke={1.5} />
    </Box>
  );

  return (
    <Group grow align="flex-start">
      <DateInput
        label={dateLabel}
        date={calendarDate}
        value={stableDateValue}
        placeholder="MM/DD/YYYY"
        valueFormat="MM/DD/YYYY"
        data-testid={dateTestId}
        onChange={handleDateChange}
        error={form.errors[dateFieldName]}
        onDateChange={handleCalendarDateChange}
        leftSection={<IconCalendar size={20} />}
      />
      <TimeInput
        withSeconds
        ref={inputRef}
        onBlur={onBlur}
        label={timeLabel}
        onFocus={onFocus}
        key={timeFieldName}
        onChange={onChange}
        value={displayValue}
        data-testid={timeTestId}
        error={form.errors[timeFieldName]}
        leftSection={timeInputLeftSection}
      />
    </Group>
  );
}
